import { getVersionForClone } from '@qr-front/ui/utils'
import type { BeforeSendFn, PostHog, Properties } from 'posthog-js'

import { env, isOnServer } from '@/utils'
import { deleteCookieClient, getCookieClient } from '@/utils/client'

import {
	LANDING_EXPERIMENT_FLAG_COOKIE,
	LANDING_EXPERIMENT_ID_COOKIE,
	LANDING_EXPERIMENT_KEY,
	LANDING_EXPERIMENT_VARIANT_COOKIE,
} from './landingExperiment'

let posthogInstance: PostHog | null = null
let initPromise: Promise<void> | null = null

// reset() clears super properties too, and identifyUser() resets mid-session
// where no page load re-runs the register() in init.
const registeredProperties: Properties = {}

// MYQRCODE-10778 AC#10 — registry's Global Properties sheet. Registered once
// via posthog.register(), which persists and rides on every event; `locale`
// is the one exception, re-registered on language switch (see UsePostHog.ts).
const GLOBAL_PROPERTIES = {
	project: getVersionForClone({ base: 'myqrcode', clone1: 'qrcreate' }),
	archetype: 'qr',
	platform: 'web',
	environment: env.ENVIRONMENT,
	app_version: env.BUILD_VERSION,
}

// Per page load, not per event — one per event would make every dropped event
// its own Person.
let fallbackDistinctId: string | null = null

/**
 * posthog-js sometimes posts an event with no distinct_id and PostHog
 * discards it. The warning names no event and no URL, so the marker below is
 * the only way to find out which events these are.
 */
const repairMissingDistinctId: BeforeSendFn = (event) => {
	if (!event || !event.properties || event.properties.distinct_id) {
		return event
	}

	if (!fallbackDistinctId) {
		fallbackDistinctId =
			posthogInstance?.get_distinct_id() ||
			`missing-distinct-id-${Date.now()}-${Math.random().toString(36).slice(2)}`
	}

	event.properties.distinct_id = fallbackDistinctId
	event.properties.missing_distinct_id_repaired = true

	return event
}

/**
 * Safe to call from multiple mount points — only the first call takes effect.
 * With NEXT_PUBLIC_POSTHOG_KEY unset (local/PR envs), this is a no-op and
 * posthog-js never makes a network request.
 *
 * posthog-js is dynamically imported here, guarded by isOnServer, rather
 * than imported at module scope: it touches `window` unconditionally on
 * load, and this module is reachable from apiClient.ts, which is part of
 * the SSR module graph — a static import here crashes every server-rendered
 * request with `ReferenceError: window is not defined`.
 *
 * NEXT_PUBLIC_POSTHOG_HOST is also required, not just the key: it's baked
 * in via the same `###VAR###` runtime placeholder-substitution as
 * NEXT_PUBLIC_PAYPAL_CLIENT_ID (see PAYMENTS.md §8), which silently
 * substitutes an empty string when the runtime env var is unset — no
 * build/deploy failure. An empty api_host makes posthog-js fall back to
 * building capture requests relative to the app's own origin (bare `/e/`
 * instead of `/ingest/e/`), which this app's middleware doesn't proxy and
 * 308-redirects — posthog-js then retries that same broken request forever
 * (MYQRCODE-10827).
 */
export const initPostHogOnce = (): Promise<void> => {
	if (isOnServer || initPromise || !env.POSTHOG_KEY || !env.POSTHOG_HOST) {
		return initPromise ?? Promise.resolve()
	}

	initPromise = import('posthog-js').then(({ default: posthog }) => {
		const landingVariant = getCookieClient(
			LANDING_EXPERIMENT_VARIANT_COOKIE,
		)
		const landingDistinctId = getCookieClient(LANDING_EXPERIMENT_ID_COOKIE)
		const landingFlagKey =
			getCookieClient(LANDING_EXPERIMENT_FLAG_COOKIE) ??
			LANDING_EXPERIMENT_KEY

		posthog.init(env.POSTHOG_KEY, {
			...(landingVariant && {
				bootstrap: {
					...(landingDistinctId && { distinctID: landingDistinctId }),
					featureFlags: { [landingFlagKey]: landingVariant },
				},
			}),
			api_host: env.POSTHOG_HOST,
			ui_host: 'https://us.posthog.com',
			// MYQRCODE-10839: 'identified_only' never created a Person for an
			// anonymous visitor, since that only happens on identify() — so a
			// funnel starting on the homepage pageview always returned zero.
			// Anonymous events are ~4x cheaper to process than identified ones,
			// but funnel visibility from first touch was judged worth the cost
			// at current traffic (~20k pageviews/day/clone).
			person_profiles: 'always',
			autocapture: false,
			// 'history_change' patches history.pushState/replaceState — Next.js
			// App Router's client-side navigation goes through the native
			// History API, so $pageview keeps firing there without a custom
			// capture() call (MYQRCODE-10778 AC#12), and stays on when
			// autocapture is off (AC#3).
			capture_pageview: 'history_change',
			capture_pageleave: true,
			capture_exceptions: true,
			before_send: repairMissingDistinctId,
		})

		posthogInstance = posthog

		if (landingVariant) {
			posthog.getFeatureFlag(landingFlagKey)
			deleteCookieClient(LANDING_EXPERIMENT_FLAG_COOKIE)
			deleteCookieClient(LANDING_EXPERIMENT_VARIANT_COOKIE)
			deleteCookieClient(LANDING_EXPERIMENT_ID_COOKIE)
		}
		Object.assign(registeredProperties, GLOBAL_PROPERTIES)
		posthog.register(GLOBAL_PROPERTIES)
	})

	return initPromise
}

// Client-only facade — safe to import from anywhere, including modules
// reachable from SSR (apiClient.ts). Never references the real posthog-js
// module at import time; no-ops until initPostHogOnce() has resolved
// (always true on the server, briefly true on the client before init).
export const posthogClient = {
	identify: (...args: Parameters<PostHog['identify']>) =>
		posthogInstance?.identify(...args),
	capture: (...args: Parameters<PostHog['capture']>) =>
		posthogInstance?.capture(...args),
	register: (...args: Parameters<PostHog['register']>) => {
		Object.assign(registeredProperties, args[0])
		return posthogInstance?.register(...args)
	},
	setPersonProperties: (
		...args: Parameters<PostHog['setPersonProperties']>
	) => posthogInstance?.setPersonProperties(...args),
	reset: (...args: Parameters<PostHog['reset']>) =>
		posthogInstance?.reset(...args),
	set_config: (...args: Parameters<PostHog['set_config']>) =>
		posthogInstance?.set_config(...args),
	getFeatureFlag: (...args: Parameters<PostHog['getFeatureFlag']>) =>
		posthogInstance?.getFeatureFlag(...args),
}

/**
 * Use this, not posthogClient.identify(). A second account on the same browser
 * reuses the first one's anon id, so PostHog refuses the merge and the session
 * stays on the wrong person. Only signOut() resets, and an account switch never
 * goes through it.
 */
export const identifyUser = ({
	id,
	email,
}: {
	id: string
	email: string
}): void => {
	if (!posthogInstance) return

	if (
		posthogInstance._isIdentified() &&
		posthogInstance.get_distinct_id() !== id
	) {
		posthogInstance.reset()
		posthogInstance.register(registeredProperties)
	}

	posthogInstance.identify(id, { email })
}
